import {
  SecondaryButton,
  Space,
  TertiaryButton,
  TextField,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardProviderOption } from '@webapp/platform/banking'
import messages from './messages'
import { useCreditCardProviderInlineCreate } from './useCreditCardProviderInlineCreate'

export interface ICreditCardProviderInlineCreateProps {
  /** Controlled new-provider name, seeded from the select's search term */
  name: string
  onNameChange: (name: string) => void
  /** The created provider, already appended to the select's options */
  onCreated: (provider: CreditCardProviderOption) => void
  onCancel: () => void
}

/**
 * Inline "Create new" form for CreditCardProviderSelect (BHV-018): the name
 * field, then its actions. Rendered by the consumer after the select's form
 * control, not inside it, so the select's error stays directly under it.
 */
export const CreditCardProviderInlineCreate = ({
  name,
  onNameChange,
  onCreated,
  onCancel,
}: ICreditCardProviderInlineCreateProps) => {
  const intl = useProcurifyIntl()

  const {
    refs: { nameInputRef },
    states: { isSaving },
    commands: { createProvider },
    methods: { handleNameKeyDown },
  } = useCreditCardProviderInlineCreate({ name, onCreated })

  return (
    <Space size='small'>
      <TextField
        id='credit-card-provider-new-name'
        inputRef={nameInputRef}
        label={intl.formatMessage(messages.nameLabel)}
        placeholder={intl.formatMessage(messages.namePlaceholder)}
        value={name}
        onChange={(event) => onNameChange(event.target.value)}
        onKeyDown={handleNameKeyDown}
        disabled={isSaving}
        fullWidth
        autoFocus
      />
      <Space direction='horizontal' justify='end' align='center'>
        <TertiaryButton type='button' onClick={onCancel}>
          {intl.formatMessage(messages.cancelButton)}
        </TertiaryButton>
        <SecondaryButton
          type='button'
          onClick={() => createProvider()}
          isLoading={isSaving}
          disabled={!name.trim()}
        >
          {intl.formatMessage(messages.createButton)}
        </SecondaryButton>
      </Space>
    </Space>
  )
}

CreditCardProviderInlineCreate.displayName = 'CreditCardProviderInlineCreate'
